// ============================================================================
//  ANNOUNCEMENT BANNER COMPONENT - PRODUCTION READY

// ============================================================================
// Shows system announcements to ALL users (authenticated and non-authenticated)
// 
// Features:
// - Works without authentication (public Firestore read)
// - Dismissible with localStorage persistence
// - Auto-expires based on expiresAt field
// - Real-time updates via Firestore onSnapshot
// - Shows on all pages
// 
// Usage: Add to App.tsx OUTSIDE of any auth-protected routes:
// 
// function App() {
//   return (
//     <>
//       <AnnouncementBanner />
//       <Routes>...</Routes>
//     </>
//   );
// }
// ============================================================================

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { X, Info, AlertTriangle, Wrench, Sparkles } from 'lucide-react';

interface Announcement {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'maintenance' | 'update';
  active: boolean;
  expiresAt?: Date | null;
  createdAt?: Date;
}

const AnnouncementBanner: React.FC = () => {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Check if this announcement was previously dismissed
  const isDismissedInStorage = (announcementId: string): boolean => {
    try {
      const dismissedAnnouncements = JSON.parse(
        localStorage.getItem('dismissedAnnouncements') || '[]'
      );
      return dismissedAnnouncements.includes(announcementId);
    } catch {
      return false;
    }
  };

  // Save dismissal to localStorage
  const saveDismissal = (announcementId: string) => {
    try {
      const dismissedAnnouncements = JSON.parse(
        localStorage.getItem('dismissedAnnouncements') || '[]'
      );
      if (!dismissedAnnouncements.includes(announcementId)) {
        dismissedAnnouncements.push(announcementId);
        // Keep only last 50 dismissals to prevent localStorage bloat
        if (dismissedAnnouncements.length > 50) {
          dismissedAnnouncements.shift();
        }
        localStorage.setItem(
          'dismissedAnnouncements',
          JSON.stringify(dismissedAnnouncements)
        );
      }
    } catch {
      // localStorage not available, ignore
    }
  };

  // Subscribe to announcements
  useEffect(() => {
    console.log('[AnnouncementBanner] Subscribing to announcements...');
    const announcementRef = doc(db, 'system', 'currentAnnouncement');

    const unsubscribe = onSnapshot(
      announcementRef,
      (snapshot) => {
        console.log('[AnnouncementBanner] Snapshot received:', snapshot.exists(), snapshot.data?.());
        
        if (snapshot.exists()) {
          const data = snapshot.data();

          // Check if announcement is active
          if (!data.active) {
            console.log('[AnnouncementBanner] Announcement not active');
            setAnnouncement(null);
            setIsVisible(false);
            return;
          }

          // Check if announcement has expired
          const expiresAt = data.expiresAt?.toDate?.();
          if (expiresAt && expiresAt < new Date()) {
            console.log('[AnnouncementBanner] Announcement expired');
            setAnnouncement(null);
            setIsVisible(false);
            return;
          }

          const announcementId = data.id || snapshot.id;

          // Check if user has dismissed this specific announcement
          if (isDismissedInStorage(announcementId)) {
            console.log('[AnnouncementBanner] Announcement dismissed by user:', announcementId);
            setDismissed(true);
            setIsVisible(false);
            return;
          }

          console.log('[AnnouncementBanner] Showing announcement:', data.title, data.message);
          setAnnouncement({
            id: announcementId,
            title: data.title || 'System Announcement',
            message: data.message || '',
            type: data.type || 'info',
            active: true,
            expiresAt: expiresAt || null,
            createdAt: data.createdAt?.toDate?.() || null,
          });
          setDismissed(false);
          setIsVisible(true);
        } else {
          console.log('[AnnouncementBanner] No announcement document exists');
          setAnnouncement(null);
          setIsVisible(false);
        }
      },
      (error) => {
        console.error('[AnnouncementBanner] Error subscribing:', error);
        // Don't show anything on error - fail silently
        setAnnouncement(null);
        setIsVisible(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // Handle dismiss
  const handleDismiss = () => {
    if (announcement?.id) {
      saveDismissal(announcement.id);
    }
    setDismissed(true);
    setIsVisible(false);
  };

  // Don't render if no announcement or dismissed
  if (!announcement || dismissed || !isVisible) {
    return null;
  }

  // Get icon and colors based on type
  const getTypeStyles = () => {
    switch (announcement.type) {
      case 'warning':
        return {
          bg: 'linear-gradient(90deg, #7c2d12 0%, #9a3412 50%, #7c2d12 100%)',
          border: '#fb923c',
          icon: <AlertTriangle size={20} />,
          iconColor: '#fb923c',
        };
      case 'maintenance':
        return {
          bg: 'linear-gradient(90deg, #4a1d96 0%, #6b21a8 50%, #4a1d96 100%)',
          border: '#a855f7',
          icon: <Wrench size={20} />,
          iconColor: '#a855f7',
        };
      case 'update':
        return {
          bg: 'linear-gradient(90deg, #065f46 0%, #047857 50%, #065f46 100%)',
          border: '#34d399',
          icon: <Sparkles size={20} />,
          iconColor: '#34d399',
        };
      case 'info':
      default:
        return {
          bg: 'linear-gradient(90deg, #1e3a5f 0%, #1e4976 50%, #1e3a5f 100%)',
          border: '#38bdf8',
          icon: <Info size={20} />,
          iconColor: '#38bdf8',
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <>
      {/* Spacer to push content down when banner is visible */}
      <div
        style={{
          width: '100%',
          background: styles.bg,
          borderBottom: `2px solid ${styles.border}`,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          animation: 'slideDown 0.3s ease-out',
          position: 'relative',
          zIndex: 9999,
        }}
      >
        {/* Icon */}
        <div style={{ color: styles.iconColor, flexShrink: 0 }}>
          {styles.icon}
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            maxWidth: '800px',
            textAlign: 'center',
          }}
        >
          {announcement.title && (
            <span
              style={{
                color: '#fff',
                fontWeight: 600,
                marginRight: '8px',
              }}
            >
              {announcement.title}:
            </span>
          )}
          <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            {announcement.message}
          </span>
        </div>

        {/* Dismiss button */}
        <button
          onClick={handleDismiss}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'rgba(255, 255, 255, 0.7)',
            flexShrink: 0,
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
          }}
          aria-label="Dismiss announcement"
        >
          <X size={16} />
        </button>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default AnnouncementBanner;